import { SiteUrls } from '@tableau/shared-urls';
import React from 'react';
import { Redirect, Route, Switch } from 'react-router-dom';

import { Messages } from '../../../../../gen-src/Localize';
import { VirtualConnectionQuery } from '../../../../../gen-src/queries';
import {
  FeatureFlagsContext,
  messageWithOrWithoutCount,
  SelectionProviderChildProps,
  SiteUrlsContext,
} from '../../../data-catalog-shared';
import { BrowserTitleUpdater, BrowserTitleUpdateType } from '../BrowserTitleUpdater';
import { ErrorListProvider } from '../error/components/ErrorListProvider';
import { ErrorToasts } from '../error/components/ErrorToasts';
import { UnrecognizedUrl } from '../error/components/UnrecognizedUrl';
import { LineageItemType } from '../lineage/LineageItemType';
import {
  getAnchorCountFromConfig,
  LineageItemConfig,
  lineageItemKey,
  LineageSection,
} from '../placePage/LineageConfiguration';
import { VirtualConnectionLineageSubplaceLayout } from '../placePage/LineageSubplaceLayout';
import {
  VirtualConnectionLineageCounts,
  VirtualConnectionLineageCountsQueryWrapper,
} from './VirtualConnectionCountsQueryWrapper';
import {
  memoizedGetVirtualConnectionLineageConfiguration,
  VirtualConnectionLineagePaneParams,
} from './VirtualConnectionLineageConfiguration';
import { VirtualConnectionQueryWrapper } from './VirtualConnectionQueryWrapper';
import { VirtualConnectionSubplaceParamsProvider } from './VirtualConnectionSubplaceParamsProvider';

export interface VirtualConnectionLineageSubplaceRouterProps {
  readonly vizportalUrlId: string;
}

export function VirtualConnectionLineageSubplaceRouter(props: VirtualConnectionLineageSubplaceRouterProps): JSX.Element {
  const { lensLineageEnabled } = React.useContext(FeatureFlagsContext);
  const urls: SiteUrls = React.useContext(SiteUrlsContext);
  const renderVirtualConnectionLineageRoute = React.useCallback((): JSX.Element => {
    return (
      <Switch>
        {memoizedGetVirtualConnectionLineageConfiguration(lensLineageEnabled).map(lineageSection =>
          lineageSection.items.map((lineageItem, i) =>
            <Route
              key={i}
              path={lineageItem.path(urls, props.vizportalUrlId)}
              render={() => renderLineageItemRoute(urls, lineageItem, lineageSection, props.vizportalUrlId)}
            />
          )
        )}
        <Redirect
          exact={true}
          from={urls.virtualConnectionLineagePath(props.vizportalUrlId)}
          to={urls.virtualConnectionLineageVirtualConnectionTablesPath(props.vizportalUrlId)}
        />
        <UnrecognizedUrl
          backToButtonLabel={Messages.BackToFields()}
          backToButtonUrl={urls.virtualConnectionLineagePath(props.vizportalUrlId)}
        />
      </Switch>
    );
  }, [props.vizportalUrlId, urls, lensLineageEnabled]);

  return (
    <SiteUrlsContext.Consumer>
      {urls =>
        <Route
          path={urls.virtualConnectionLineagePath(props.vizportalUrlId)}
          render={() => renderVirtualConnectionLineageRoute()}
        />
      }
    </SiteUrlsContext.Consumer>
  );
}

function renderLineageItemRoute(
  urls: SiteUrls,
  lineageItem: LineageItemConfig<VirtualConnectionLineageCounts, VirtualConnectionLineagePaneParams>,
  lineageSection: LineageSection<VirtualConnectionLineageCounts, VirtualConnectionLineagePaneParams>,
  vizportalUrlId: string,
): JSX.Element {
  return (
    <VirtualConnectionSubplaceParamsProvider>
      {params =>
        <ErrorListProvider>
          <ErrorToasts />
          <VirtualConnectionQueryWrapper vizportalUrlId={vizportalUrlId}>
            {virtualConnection =>
              <VirtualConnectionLineageCountsQueryWrapper
                vizportalUrlId={vizportalUrlId}
                selectedTableKeys={params.selectedTables}
              >
                {({ countsLoading, unfilteredCounts, filteredCounts }) =>
                  <>
                    <VirtualConnectionLineageSubplace
                      vizportalUrlId={vizportalUrlId}
                      virtualConnection={virtualConnection}
                      lineageItem={lineageItem}
                      lineageSection={lineageSection}
                      countsLoading={countsLoading}
                      unfilteredCounts={unfilteredCounts}
                      filteredCounts={filteredCounts}
                      urlSearchValue={params.urlSearchValue}
                      selectedKeys={params.selectedTables}
                      updateSelectionForKey={params.updateTableSelection}
                      clearTableSelection={params.clearTableSelection}
                    />
                  </>
                }
              </VirtualConnectionLineageCountsQueryWrapper>
            }
          </VirtualConnectionQueryWrapper>
        </ErrorListProvider>
      }
    </VirtualConnectionSubplaceParamsProvider>
  );
}

type VirtualConnectionLineageSubplaceProps = {
  readonly vizportalUrlId: string;
  readonly virtualConnection: Unpacked<VirtualConnectionQuery['virtualConnections']>;
  readonly lineageItem: LineageItemConfig<VirtualConnectionLineageCounts, VirtualConnectionLineagePaneParams>;
  readonly lineageSection: LineageSection<VirtualConnectionLineageCounts, VirtualConnectionLineagePaneParams>;
  readonly countsLoading: boolean;
  readonly unfilteredCounts: VirtualConnectionLineageCounts | null;
  readonly filteredCounts: VirtualConnectionLineageCounts | null;
  readonly urlSearchValue: string;
  readonly clearTableSelection: () => void;
} & SelectionProviderChildProps;

function VirtualConnectionLineageSubplace(props: VirtualConnectionLineageSubplaceProps) {
  const featureFlags = React.useContext(FeatureFlagsContext);
  const selectedTables = Object.keys(props.selectedKeys);
  const hasFilter = selectedTables.length > 0;
  const filteredTableId: string | null = hasFilter ? selectedTables[0] : null;
  const lineageCounts = props.filteredCounts ? props.filteredCounts : props.unfilteredCounts;
  return (
    <>
      <BrowserTitleUpdater
        name={props.virtualConnection.name}
        browserTitleUpdateType={BrowserTitleUpdateType.TITLE_WITH_ENTITY_NAME_AND_SUBSPACE_NAME}
        subspaceName={Messages.LineageNavHeader()}
      />
      <VirtualConnectionLineageSubplaceLayout
        activeLineageItemKey={lineageItemKey(props)}
        selectedAnchorItemIds={selectedTables}
        pane={props.lineageItem.paneFactory({
          title: messageWithOrWithoutCount(
            lineageCounts && props.lineageItem.count(lineageCounts),
            props.lineageItem.messagesForPane
          ),
          selectedKeys: props.selectedKeys,
          updateSelectionForKey: props.updateSelectionForKey,
          vizportalUrlId: props.vizportalUrlId,
          filteredTableId,
          count: lineageCounts && props.lineageItem.count(lineageCounts),
          virtualConnectionLuid: props.virtualConnection.luid,
        })}
        virtualConnectionId={props.vizportalUrlId}
        countsLoading={props.countsLoading}
        unfilteredCounts={props.unfilteredCounts}
        filteredCounts={props.filteredCounts}
        urlSearchValue={props.urlSearchValue}
        clearTableSelection={props.clearTableSelection}
        anchorName={props.virtualConnection.name}
        anchorCount={getAnchorCountFromConfig(
          memoizedGetVirtualConnectionLineageConfiguration(featureFlags.lensLineageEnabled),
          props.unfilteredCounts
        )}
        filteredOnItemType={hasFilter ? LineageItemType.VirtualConnection : null}
        filteredItemName={props.filteredCounts?.filteredFieldName ?? null}
        lineageSectionType={props.lineageSection.sectionType}
      />
    </>
  );
}
